import { useUnit } from 'effector-react'
import type { AppProps as NextAppProps } from 'next/app'
import { useRouter } from 'next/router'
import React from 'react'

import { translations } from '@portal/i18n'
import { css } from '@portal/styled-system/css'

import { Maintenance } from '@/pages/play'
import { SsoAuthCookieScript } from '@/shared/api/ssoauth'
import { env } from '@/shared/config'
import { appStarted } from '@/shared/lib/app'
import { useMountCallback, usePortalMaintenance } from '@/shared/lib/hooks'
import { langUpdated } from '@/shared/lib/i18n'
import { getRouterLanguage } from '@/shared/lib/language'
import { routerUpdated } from '@/shared/lib/next'

import { AppHead } from './app-head'
import './app-init'
import { Providers } from './providers'
import { StylesProvider } from './styles-provider'

export function PortalApp({ Component, pageProps }: NextAppProps) {
    const router = useRouter()

    const lang = getRouterLanguage(router) ?? env.LANG_FALLBACK
    const i18n = translations[lang]

    const handleAppStart = useUnit(appStarted)
    const handleLangUpdate = useUnit(langUpdated)
    const handleRouterUpdate = useUnit(routerUpdated)

    React.useEffect(() => {
        handleLangUpdate({ i18n, lang })
    }, [handleLangUpdate, i18n, lang])

    React.useEffect(() => {
        handleRouterUpdate(router)
    }, [handleRouterUpdate, router])

    React.useEffect(() => {
        document.documentElement.lang = lang
    }, [lang])

    useMountCallback(handleAppStart)

    const isMaintenance = usePortalMaintenance()
    if (isMaintenance) {
        return (
            <div className={css({ height: '[100vh]' })}>
                <Maintenance type="technical" />
            </div>
        )
    }

    return (
        <React.Fragment>
            <SsoAuthCookieScript />

            <StylesProvider lang={lang} />

            <AppHead i18n={i18n} />

            <Providers i18n={i18n} lang={lang}>
                <Component {...pageProps} />
            </Providers>
        </React.Fragment>
    )
}
